import moment from 'moment';
import { DATE_FORMATS } from './constants';
import { FORMPF_NUMERIC_TO_ISO } from '../../../../../../consts/formPFCurrencies';

const FORMPF_NUMERIC_CODE_MAP = Object.fromEntries(
  FORMPF_NUMERIC_TO_ISO.map(c => [c.value, c.label])
);

/**
 * Transforms raw transformation data rows by replacing Form PF numeric currency codes
 * in the "Base currency" column with their human-readable labels.
 * Row 0 is always the header row — keys whose value is 'Base currency' are found there.
 */
export const mapBaseCurrencyCodes = data => {
  if (!Array.isArray(data) || data.length === 0) return data;
  const headerRow = data[0];
  const baseCurrencyKey = Object.keys(headerRow).find(
    key => headerRow[key]?.toLowerCase() === 'base currency'
  );
  if (!baseCurrencyKey) return data;
  return data.map((row, idx) => {
    if (idx === 0) return row;
    const raw = row[baseCurrencyKey];
    if (typeof raw !== 'string' || !(raw in FORMPF_NUMERIC_CODE_MAP)) return row;
    return { ...row, [baseCurrencyKey]: FORMPF_NUMERIC_CODE_MAP[raw] };
  });
};

export const checkValueToZero = val => val === 0 || val === '0' || val === '0.0' || val === '0.00';

export const isItemDateLike = item => {
  try {
    const text = item?.text || item?.value;

    if (typeof text !== 'string' || text.trim() === '') return false;

    // Try strict matching first
    if (moment(text, DATE_FORMATS, true).isValid()) {
      return true;
    }

    // Fallback: loose Moment parse (catches some cases like "Feb 29 2024")
    return moment(text).isValid();
  } catch (err) {
    console.log(`Error in isItemDateLike ${err}`);
    return false;
  }
};

/**
 * // This function is used for extraction only 1 cell can be chosen for each row
 * // e.g. Exposures->Instrument Name
 * // given targetColumnValues = [{rowId: 1, columnId: 1, text: 'Amazon'}]
 * // given items = [{rowId: 1, columnId: 2, text: 'Datox'}]
 * // result will be = [{rowId: 1, columnId: 2, text: 'Datox'}]
 * @param targetColumnValues
 * @param items
 * @returns {*[]}
 */
export const getSingleCellForEachRow = (targetColumnValues, items) => {
  try {
    const updatedTargetColumnValues = [...targetColumnValues];

    items.forEach(item => {
      const existingIndex = updatedTargetColumnValues.findIndex(v => v.rowId === item.rowId);
      // if the row already had a chosen cell, the previous choice will be removed
      if (existingIndex !== -1) {
        updatedTargetColumnValues.splice(existingIndex, 1);
      }
      updatedTargetColumnValues.push(item);
    });

    return updatedTargetColumnValues;
  } catch (err) {
    console.log(`Error in getSingleCellForEachRow ${err}`);
    return [];
  }
};

// eg1 activeFundChildValue?.type = ['null', 'string']
// selectedItem = { text: 'Hello world' } - valid
// -------
// eg2 activeFundChildValue?.type = ['null', 'string']
// selectedItem = { text: 1234 } - invalid
export const getDataTypeOfSelectedItemInNonLookupTable = selectedItem => {
  if (selectedItem.text !== null && selectedItem.text !== undefined)
    return typeof selectedItem.text;
  if (selectedItem.value !== null && selectedItem.value !== undefined)
    return typeof selectedItem.value;
  return 'null';
};

// JSON Schema types (e.g. 'integer', 'boolean') don't always match JS typeof values

const SCHEMA_TYPE_TO_JS_TYPES = {
  integer: ['number'],
  boolean: ['boolean', 'string', 'number']
};

export const schemaTypesIncludeJsType = (schemaTypes, jsType) => {
  if (!schemaTypes) return false;
  const normalizedSchemaTypes = Array.isArray(schemaTypes) ? schemaTypes : [schemaTypes];
  return normalizedSchemaTypes.some(schemaType => {
    const mapped = SCHEMA_TYPE_TO_JS_TYPES[schemaType];
    if (mapped) return mapped.includes(jsType);
    return schemaType === jsType;
  });
};

export function getUniqueColumnsWithValues(dataArray) {
  const uniqueColumns = {};
  const columnTypes = {};

  dataArray?.forEach(data => {
    data.tables?.forEach(table => {
      table.columns.forEach(column => {
        const nonEmptyValues = column.values
          .map(value => value)
          .filter(value => value.text !== null && value.text !== '');

        columnTypes[column.name] = column;

        if (nonEmptyValues.length > 0) {
          if (!uniqueColumns[column.name]) {
            uniqueColumns[column.name] = new Set();
          }

          nonEmptyValues.forEach(value => {
            uniqueColumns[column.name].add(value);
          });
        }
      });
    });
  });

  // Convert the Set to an array for each column
  const result = Object.keys(uniqueColumns).map(columnName => ({
    ...(columnTypes[columnName] || {}),
    columnName,
    values: Array.from(uniqueColumns[columnName])
  }));

  return result;
}

export function getUniqueLookupColumnsWithValues(dataArray) {
  const uniqueColumns = {};
  const columnTypes = {};

  dataArray?.forEach(data => {
    data.columns.forEach(column => {
      const nonEmptyValues = column.values
        .map(value => value)
        .filter(value => value.text !== null && value.text !== '');

      columnTypes[column.name] = column;

      if (nonEmptyValues.length > 0) {
        if (!uniqueColumns[column.name]) {
          uniqueColumns[column.name] = new Set();
        }

        nonEmptyValues.forEach(value => {
          uniqueColumns[column.name].add(value);
        });
      }
    });
  });

  // Convert the Set to an array for each column
  const result = Object.keys(uniqueColumns).map(columnName => ({
    ...(columnTypes[columnName] || {}),
    columnName,
    values: Array.from(uniqueColumns[columnName])
  }));

  return result;
}
